import React from "react";
import {
  Box,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";

import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";

import AdminSidebar from "../../../../components/Administration/AdminSidebar";

/*
 * Temporary organization overview.
 *
 * TODO:
 * Replace the hardcoded dashboard content below with the real organization
 * dashboard when that integration is ready.
 */

const ui = {
  pageBg: "#f7f8fa",
  panel: "#ffffff",
  border: "#e6e9ee",
  text: "#182230",
  muted: "#667085",
  subtle: "#98a2b3",
};

const panelSx = {
  border: `1px solid ${ui.border}`,
  borderRadius: 2,
  bgcolor: ui.panel,
  boxShadow: "0 1px 2px rgba(16,24,40,.035), 0 8px 24px rgba(16,24,40,.018)",
};

const MetricCard = ({ icon, label, value, secondary }) => (
  <Paper elevation={0} sx={{ ...panelSx, p: 1.7, height: "100%" }}>
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: 1.5,
          display: "grid",
          placeItems: "center",
          bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
          color: "primary.main",
          flexShrink: 0,
          "& svg": { fontSize: 20 },
        }}
      >
        {icon}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ color: ui.muted, fontSize: 11.5, fontWeight: 500 }}>
          {label}
        </Typography>
        <Typography
          sx={{
            mt: 0.25,
            color: ui.text,
            fontSize: 23,
            fontWeight: 560,
            lineHeight: 1.1,
            letterSpacing: "-0.02em",
          }}
        >
          {value}
        </Typography>
        {secondary && (
          <Typography sx={{ mt: 0.4, color: ui.subtle, fontSize: 10.5 }}>
            {secondary}
          </Typography>
        )}
      </Box>
    </Stack>
  </Paper>
);

const SimpleTrend = () => (
  <Box sx={{ mt: 1.5 }}>
    <svg
      viewBox="0 0 520 170"
      width="100%"
      height="170"
      role="img"
      aria-label="Temporary usage trend"
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id="uqOverviewFill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.16" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.01" />
        </linearGradient>
      </defs>

      {[35, 75, 115, 155].map(y => (
        <line
          key={y}
          x1="0"
          x2="520"
          y1={y}
          y2={y}
          stroke="#eef0f3"
          strokeWidth="1"
        />
      ))}

      <g style={{ color: "var(--uq-overview-chart-color)" }}>
        <path
          d="M0,135 C55,128 88,120 125,113 C170,103 200,105 245,88 C285,73 330,82 370,61 C415,43 455,49 520,28 L520,170 L0,170 Z"
          fill="url(#uqOverviewFill)"
        />
        <path
          d="M0,135 C55,128 88,120 125,113 C170,103 200,105 245,88 C285,73 330,82 370,61 C415,43 455,49 520,28"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        />
        {[[0,135],[125,113],[245,88],[370,61],[520,28]].map(([x,y]) => (
          <circle
            key={`${x}-${y}`}
            cx={x}
            cy={y}
            r="4"
            fill="#fff"
            stroke="currentColor"
            strokeWidth="3"
          />
        ))}
      </g>
    </svg>

    <Stack direction="row" justifyContent="space-between" sx={{ mt: -0.4 }}>
      {["Apr", "Maj", "Jun", "Jul", "Aug"].map(month => (
        <Typography key={month} sx={{ color: ui.subtle, fontSize: 10.5 }}>
          {month}
        </Typography>
      ))}
    </Stack>
  </Box>
);


const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
    background: "#f7f8fa",
  },
}));

const OrganizationOverviewPage = () => {
  const classes = useStyles();
  return (
  <Box className={classes.root}>
    <Grid container spacing={2}>
      <Grid item xs={12} md={2}>
        <AdminSidebar />
      </Grid>

      <Grid item xs={12} md={9} lg={10} sx={{ minWidth: 0 }}>
        <Box sx={{ maxWidth: 1320, mx: "auto" }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1}
            sx={{ mb: 1.5 }}
          >
            <Box>
              <Typography
                component="h1"
                sx={{
                  color: ui.text,
                  fontSize: { xs: 20, md: 22 },
                  fontWeight: 560,
                  lineHeight: 1.2,
                  letterSpacing: "-0.018em",
                }}
              >
                Organization dashboard
              </Typography>

              <Typography sx={{ mt: 0.35, color: ui.muted, fontSize: 12.5 }}>
                Översikt över organisationens användning, kapacitet och status.
              </Typography>
            </Box>

            <Chip
              size="small"
              label="Tillfällig dashboard"
              sx={{
                height: 26,
                borderRadius: 1,
                bgcolor: theme => alpha(theme.palette.primary.main, 0.07),
                color: "primary.main",
                fontSize: 10.8,
                fontWeight: 550,
              }}
            />
          </Stack>

          <Grid container spacing={1.25}>
            <Grid item xs={12} sm={6} xl={3}>
              <MetricCard
                icon={<PeopleAltOutlinedIcon />}
                label="Användare"
                value="432"
                secondary="+6,3 % senaste månaden"
              />
            </Grid>

            <Grid item xs={12} sm={6} xl={3}>
              <MetricCard
                icon={<WorkspacesOutlinedIcon />}
                label="Arbetsytor"
                value="7"
                secondary="1 ny denna månad"
              />
            </Grid>

            <Grid item xs={12} sm={6} xl={3}>
              <MetricCard
                icon={<StorageOutlinedIcon />}
                label="Lagring"
                value="57 GB"
                secondary="av 100 GB"
              />
            </Grid>

            <Grid item xs={12} sm={6} xl={3}>
              <MetricCard
                icon={<ExtensionOutlinedIcon />}
                label="Aktiva moduler"
                value="2 av 2"
                secondary="Alla tillgängliga är aktiva"
              />
            </Grid>

            <Grid item xs={12} lg={8}>
              <Paper
                elevation={0}
                sx={{
                  ...panelSx,
                  p: { xs: 1.4, sm: 1.75 },
                  height: "100%",
                  "--uq-overview-chart-color": theme => theme.palette.primary.main,
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  spacing={1}
                >
                  <Box>
                    <Typography sx={{ color: ui.text, fontSize: 14.5, fontWeight: 560 }}>
                      Användning över tid
                    </Typography>
                    <Typography sx={{ mt: 0.2, color: ui.muted, fontSize: 11 }}>
                      Tillfällig visualisering
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <TrendingUpOutlinedIcon sx={{ fontSize: 16, color: "success.main" }} />
                    <Typography sx={{ fontSize: 11, color: "success.main", fontWeight: 550 }}>
                      +4,8 %
                    </Typography>
                  </Stack>
                </Stack>

                <SimpleTrend />
              </Paper>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Paper elevation={0} sx={{ ...panelSx, p: { xs: 1.4, sm: 1.75 }, height: "100%" }}>
                <Typography sx={{ color: ui.text, fontSize: 14.5, fontWeight: 560 }}>
                  Kapacitet
                </Typography>

                <Box sx={{ mt: 1.6 }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ color: ui.muted, fontSize: 11.5 }}>Lagring</Typography>
                    <Typography sx={{ color: ui.text, fontSize: 11.5, fontWeight: 550 }}>
                      57 %
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={57}
                    sx={{
                      mt: 0.7,
                      height: 6,
                      borderRadius: 6,
                      bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                      "& .MuiLinearProgress-bar": { borderRadius: 6 },
                    }}
                  />
                </Box>

                <Box sx={{ mt: 1.8 }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ color: ui.muted, fontSize: 11.5 }}>
                      Aktiva användare
                    </Typography>
                    <Typography sx={{ color: ui.text, fontSize: 11.5, fontWeight: 550 }}>
                      64 %
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={64}
                    sx={{
                      mt: 0.7,
                      height: 6,
                      borderRadius: 6,
                      bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                      "& .MuiLinearProgress-bar": { borderRadius: 6 },
                    }}
                  />
                </Box>

                <Box sx={{ mt: 2, pt: 1.4, borderTop: `1px solid ${ui.border}` }}>
                  <Stack direction="row" spacing={0.8} alignItems="center">
                    <CheckCircleOutlineRoundedIcon sx={{ fontSize: 17, color: "success.main" }} />
                    <Box>
                      <Typography sx={{ color: ui.text, fontSize: 11.7, fontWeight: 550 }}>
                        Organisationen är aktiv
                      </Typography>
                      <Typography sx={{ mt: 0.1, color: ui.muted, fontSize: 10.6 }}>
                        Inga kapacitetsvarningar just nu.
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  ...panelSx,
                  p: { xs: 1.4, sm: 1.75 },
                  borderStyle: "dashed",
                  bgcolor: theme => alpha(theme.palette.primary.main, 0.018),
                }}
              >
                <Typography sx={{ color: ui.text, fontSize: 12.5, fontWeight: 550 }}>
                  Dashboard-placeholder
                </Typography>
                <Typography sx={{ mt: 0.3, color: ui.muted, fontSize: 11.3, lineHeight: 1.5 }}>
                  Den här sidan är tillfällig. När den riktiga dashboard-komponenten är klar
                  kan hela innehållet i huvudytan ersättas utan att meny eller sidstruktur
                  behöver ändras.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Grid>
    </Grid>
  </Box>)
};

export default OrganizationOverviewPage;
